import { useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import MyTextField from "../../components/inputs/MyTextField";
import MyButton from "../../components/MyButton";
import MyCheckboxCircle from "../../components/inputs/MyCheckboxCircle";
import Privacy from "../Privacy";
import Term from "../Term";
import MyInputCaptcha from "../../components/inputs/MyInputCaptcha";
import { useAuth } from "../../hooks/useAuth";
import { SiteContext } from "../../context/SiteProvider";

const Title = ({ title, className = "" }) => {
  return (
    <div className={`md:pb-3 pb-2 border-b border-primary ${className}`}>
      <h6 className="flex items-center gap-3 font-semibold md:text-xl">
        <img
          src="/assets/img/point.png"
          alt=""
          className="md:w-[34px] w-[24px] h-auto"
        />
        {title}
      </h6>
    </div>
  );
};

const userIdRegex = /^[a-z0-9]+$/;

const Register = () => {
  const navigate = useNavigate();
  const {
    checkUserIdMutation,
    checkEmailMutation,
    confirmEmailMutation,
    signUpMutation,
  } = useAuth();
  const { setLoadingScreen } = useContext(SiteContext);

  const initValues = {
    password: "",
    rePassword: "",
    userName: "",
    phone: "",
    organization: "",
    captchaToken: "",
    agreeToTerm: false,
    agreeToPrivacy: false,
    isCheckId: false,
    userId: "",
    userKeyToken: "",
    isSentOtp: false,
    isConfirmedOtp: false,
    email: "",
    otp: "",
  };
  const validationSchema = yup.object({
    agreeToTerm: yup
      .string()
      .oneOf(['동의'], ["필수 동의 항목이며, 미동의 시 가입이 제한됩니다."])
      .required("필수 동의 항목이며, 미동의 시 가입이 제한됩니다."),
    agreeToPrivacy: yup
      .string()
      .oneOf(['동의'], ["필수 동의 항목이며, 미동의 시 가입이 제한됩니다."])
      .required("필수 동의 항목이며, 미동의 시 가입이 제한됩니다."),
    password: yup
      .string()
      .min(6, "비밀번호 생성 규칙에 맞게 다시 입력해주세요.")
      .max(16, "비밀번호 생성 규칙에 맞게 다시 입력해주세요.")
      .matches(
        /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).+$/,
        "비밀번호 생성 규칙에 맞게 다시 입력해주세요."
      )
      .required("필수 입력 항목입니다."),
    rePassword: yup
      .string()
      .required("필수 입력 항목입니다.")
      .oneOf([yup.ref("password")], "비밀번호가 일치하지 않습니다."),
    userName: yup.string().required("필수 입력 항목입니다."),
    phone: yup
      .string()
      .required("전화번호는 필수 입력 항목입니다.")
      .matches(/^010-\d{3,4}-\d{4}$/, "010-1234-5678 형식으로 입력해주세요."),
    isCheckId: yup
      .boolean()
      .oneOf([true], "신분증을 확인해 주세요.")
      .required("신분증을 확인해 주세요.")
      .default(false),
    userId: yup
      .string()
      .required("필수 입력 항목입니다.")
      .min(6, "6~20자의 영문 소문자와 숫자만 사용 가능합니다.")
      .max(20, "6~20자의 영문 소문자와 숫자만 사용 가능합니다.")
      .matches(userIdRegex, "6~20자의 영문 소문자와 숫자만 사용 가능합니다.")
      .when(["isCheckId"], {
        is: false,
        then: (schema) =>
          schema.test(
            "check-id",
            "아이디 중복확인을 진행해주세요.",
            () => false
          ),
      }),
    email: yup
      .string()
      .email("이메일 형식이 올바르지 않습니다.")
      .required("이메일을 입력해주세요."),
    isSentOtp: yup.boolean().default(false),
    otp: yup.string().when("isSentOtp", {
      is: true,
      then: () =>
        yup
          .string()
          .required("필수 입력 항목입니다.")
          .when(["isConfirmedOtp"], {
            is: false,
            then: (schema) =>
              schema.test(
                "check-otp",
                "이메일 인증을 진행해주세요.",
                () => false
              ),
          }),
    }),
    isConfirmedOtp: yup.boolean().default(false),
    captcha: yup.string().required("자동입력방지 인증코드를 입력해주세요."),
  });
  const methods = useForm({
    defaultValues: initValues,
    resolver: yupResolver(validationSchema),
  });

  const isSentOtp = methods.watch("isSentOtp");
  const isCheckId = methods.watch("isCheckId");
  const isConfirmedOtp = methods.watch("isConfirmedOtp");

  // user Id
  const onCheckUserId = async () => {
    const userId = methods.getValues("userId");

    if (
      !userId ||
      userId.length < 6 ||
      userId.length > 20 ||
      !userIdRegex.test(userId)
    ) {
      await methods.trigger("userId");
    } else {
      setLoadingScreen(true);
      const payload = {
        userKeyType: "Id",
        userKeyCode: methods.getValues("userId"),
      };
      checkUserIdMutation.mutate(payload, {
        onSuccess: (res) => {
          if (!res.exists) {
            methods.setValue("isCheckId", true);
            methods.clearErrors("userId");
            methods.clearErrors("isCheckId");
          } else {
            methods.setError("userId", {
              type: "manual",
              message: "사용할 수 없는 아이디입니다.",
            });
            return;
          }
        },
        onSettled: () => {
          setLoadingScreen(false);
        },
      });
    }
  };

  // Email
  const onCheckEmail = async () => {
    const email = methods.getValues("email");
    if (!email) {
      await methods.trigger("email");
    } else {
      const email = methods.getValues("email");
      setLoadingScreen(true);

      const payload = {
        userKeyType: "Email",
        userKeyCode: email,
      };
      checkUserIdMutation.mutate(payload, {
        onSuccess: (res) => {
          if (res.exists) {
            methods.setError("email", {
              type: "manual",
              message: "이미 등록된 이메일 주소입니다",
            });
            setLoadingScreen(false);
            return;
          } else {
            checkEmailMutation.mutate(payload, {
              onSuccess: (res) => {
                if (res?.userKeyToken) {
                  alert(
                    "입력하신 이메일주소로 인증번호가 발송되었습니다. 메일함을 확인해주세요."
                  );
                  methods.clearErrors("email");
                  methods.setValue("isSentOtp", true);
                  methods.setValue("userKeyToken", res.userKeyToken);
                  methods.trigger("otp");
                } else {
                  alert("이메일 전송 중 오류가 발생했습니다.");
                }
              },
              onError: () => {
                alert("이메일 전송 중 오류가 발생했습니다.");
              },
              onSettled: () => setLoadingScreen(false),
            });
          }
        },
      });
    }
  };

  const onConfirmOtp = async () => {
    const otp = methods.getValues("otp");

    if (!otp) {
      await methods.trigger("otp");
    } else {
      const payload = {
        userKeyToken: methods.getValues("userKeyToken"),
        pincode: methods.getValues("otp"),
      };

      setLoadingScreen(true);
      confirmEmailMutation.mutate(payload, {
        onSuccess: (res) => {
          if (res?.userKeyToken) {
            alert("이메일 인증이 완료되었습니다.");
            methods.setValue("isConfirmedOtp", true);
            methods.setValue("userKeyTokenForEmail", res.userKeyToken);
            methods.clearErrors("otp");
            methods.clearErrors("isConfirmedOtp");
          } else {
            methods.setError("otp", {
              message: "인증번호를 다시 확인해주세요.",
            });
            return;
          }
        },
        onError: () => {
          methods.setError("otp", {
            message: "인증번호를 다시 확인해주세요.",
          });
          return;
        },
        onSettled: () => setLoadingScreen(false),
      });
    }
  };

  const onSubmit = (data) => {
    const payload = {
      id: data.userId,
      userName: data.userName,
      password: data.password,
      profile: {
        organization: data.organization,
        privacyAgree: data.agreeToPrivacy,
        termsAgree: data.agreeToTerm,
        phone: data.phone,
      },
    };

    if (data.email) {
      if (!data.isSentOtp) {
        methods.setError("email", { message: "이메일을 확인해주세요." });
        return;
      }
      if (data.otp && !data.isConfirmedOtp) {
        methods.setError("otp", { message: "OTP를 먼저 인증해주세요." });
        return;
      }
      payload.userKeyTokenForEmail = data.userKeyTokenForEmail;
    }

    if (!data.captchaToken) {
      methods.setError("captcha", {
        message: "인증을 진행해주세요.",
      });
      return;
    }
    payload.captchaToken = data.captchaToken;

    setLoadingScreen(true);

    signUpMutation.mutate(payload, {
      onSuccess: () => {
        alert("회원가입이 완료되었습니다.");
        backToLoginPage();
      },
      onError: () => {
        alert("등록에 실패했습니다. 다시 시도해 주세요.");
      },
      onSettled: () => {
        setLoadingScreen(false);
      },
    });
  };

  const backToLoginPage = () => {
    navigate("/Login");
  };

  return (
    <div className="lg:pt-[180px] md:pt-[100px] lg:pb-20 md:pb-10 pt-[76px] pb-4">
      <div className="container flex justify-center">
        <div className="max-w-[820px] w-full">
          <h2 className="lg:mb-14 md:mb-10 sm:mb-6 mb-4 font-bold lg:text-4xl sm:text-3xl text-xl text-center">
            회원가입
          </h2>

          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <Title title="기본 정보 입력" className="md:mb-5 mb-3" />
              <div className="flex flex-wrap md:mb-5 mb-3">
                <label
                  htmlFor="userId"
                  className="md:mt-4 md:w-[180px] w-full font-semibold"
                >
                  아이디 <span className="text-error">*</span>
                </label>
                <div className="flex-1">
                  <MyTextField
                    id="userId"
                    name="userId"
                    placeholder="아이디를 입력해주세요."
                    onChange={() => methods.setValue("isCheckId", false)}
                  />
                  {isCheckId && (
                    <p className="text-sm text-success">
                      사용 가능한 아이디입니다.
                    </p>
                  )}
                </div>

                <MyButton
                  className="md:w-[120px] md:ml-5 xs:w-max w-full xs:ml-2 xs:mt-0 mt-2"
                  variant="outlined"
                  style={{ height: 56 }}
                  onClick={onCheckUserId}
                >
                  <span className="text-gray-dark text-sm font-semibold">
                    중복 확인
                  </span>
                </MyButton>
              </div>

              <div className="flex flex-wrap md:mb-5 mb-3">
                <label
                  htmlFor="password"
                  className="md:mt-4 md:w-[180px] w-full font-semibold"
                >
                  비밀번호 <span className="text-error">*</span>
                </label>
                <div className="flex-1">
                  <MyTextField
                    id="password"
                    type="password"
                    name="password"
                    placeholder="비밀번호를 입력해주세요."
                    hint="영문, 숫자, 특수문자를 포함하여 6자리 이상, 16자리 미만으로 입력해주세요."
                  />
                </div>
                <div className="w-[120px] ml-5 md:block hidden" />
              </div>

              <div className="flex flex-wrap md:mb-5 mb-3">
                <label
                  htmlFor="rePassword"
                  className="md:mt-4 md:w-[180px] w-full font-semibold"
                >
                  비밀번호 확인 <span className="text-error">*</span>
                </label>
                <div className="flex-1">
                  <MyTextField
                    id="rePassword"
                    type="password"
                    name="rePassword"
                    placeholder="비밀번호를 다시 한번 입력해주세요."
                  />
                </div>
                <div className="w-[120px] ml-5 md:block hidden" />
              </div>

              <div className="flex flex-wrap md:mb-5 mb-3">
                <label
                  htmlFor="userName"
                  className="md:mt-4 md:w-[180px] w-full font-semibold"
                >
                  이름 <span className="text-error">*</span>
                </label>
                <div className="flex-1">
                  <MyTextField
                    id="userName"
                    name="userName"
                    placeholder="이름을 입력해주세요."
                  />
                </div>
                <div className="w-[120px] ml-5 md:block hidden" />
              </div>

              <div className="flex flex-wrap md:mb-5 mb-3">
                <label
                  htmlFor="email"
                  className="md:mt-4 md:w-[180px] w-full font-semibold"
                >
                  이메일 주소
                  <span className="text-error"> *</span>
                </label>
                <div className="flex-1">
                  <MyTextField
                    id="email"
                    name="email"
                    placeholder="이메일 주소를 입력해주세요."
                    onChange={() => methods.setValue("isSentOtp", false)}
                  />
                </div>
                <MyButton
                  className="md:w-[120px] md:ml-5 xs:w-max w-full xs:ml-2 xs:mt-0 mt-2"
                  variant="outlined"
                  style={{ height: 56 }}
                  onClick={onCheckEmail}
                >
                  <span className="text-gray-dark text-sm font-semibold">
                    인증하기
                  </span>
                </MyButton>
              </div>

              {isSentOtp && (
                <div className="flex flex-wrap md:mb-5 mb-3">
                  <div className="md:min-w-[180px] md:block hidden" />
                  <div className="flex-1">
                    <MyTextField
                      id="otp"
                      name="otp"
                      placeholder="인증번호를 입력해주세요."
                      onChange={() => methods.setValue("isConfirmedOtp", false)}
                    />
                    {isConfirmedOtp && (
                      <p className="text-success text-sm">
                        인증번호가 확인되었습니다.
                      </p>
                    )}
                  </div>
                  <MyButton
                    className="md:w-[120px] md:ml-5 xs:w-max w-full xs:ml-2 xs:mt-0 mt-2"
                    variant="outlined"
                    style={{ height: 56 }}
                    onClick={onConfirmOtp}
                  >
                    <span className="text-gray-dark text-sm font-semibold">
                      확인
                    </span>
                  </MyButton>
                </div>
              )}

              <div className="flex flex-wrap md:mb-5 mb-3">
                <label
                  htmlFor="phone"
                  className="md:mt-4 md:w-[180px] w-full font-semibold"
                >
                  연락처
                </label>
                <div className="flex-1">
                  <MyTextField
                    id="phone"
                    name="phone"
                    placeholder="연락처를 입력해주세요."
                  />
                </div>
                <div className="w-[120px] ml-5 md:block hidden" />
              </div>

              <div className="flex flex-wrap md:mb-5 mb-3">
                <label
                  htmlFor="organization"
                  className="md:mt-4 md:w-[180px] w-full font-semibold"
                >
                  소속기관
                </label>
                <div className="flex-1">
                  <MyTextField
                    id="organization"
                    name="organization"
                    placeholder="연구자 페이지 접근을 위해 소속기관 입력이 필요합니다."
                  />
                </div>
                <div className="w-[120px] ml-5 md:block hidden" />
              </div>

              <MyInputCaptcha
                name="captchaToken"
                labelClass="md:w-[180px] w-full"
              />

              <Title
                title="사이트 이용약관"
                className="md:mt-10 mt-5 md:mb-5 mb-3"
              />

              <div className="border border-gray-border overflow-y-auto small-scroll max-h-[200px] rounded md:p-5 p-3">
                <Term />
              </div>

              <div className="flex justify-end mt-3 md:mb-20 sm:mb-10 mb-5">
                <MyCheckboxCircle
                  name="agreeToTerm"
                  label="이용약관에 동의합니다."
                  onChange={(val) => methods.setValue("agreeToTerm", val ? "동의" : "")}
                  required
                />
              </div>

              <Title title="개인정보처리방침" className="md:mb-5 mb-3" />

              <div className="border border-gray-border overflow-y-auto small-scroll max-h-[200px] rounded md:p-5 p-3">
                <Privacy />
              </div>

              <div className="flex justify-end mt-3 md:mb-10 mb-5">
                <MyCheckboxCircle
                  name="agreeToPrivacy"
                  label="개인정보 수집 및 이용에 동의합니다."
                  onChange={(val) => methods.setValue("agreeToPrivacy", val ? "동의" : "")}
                  required
                />
              </div>

              <div className="mt-5 flex justify-center items-stretch gap-5">
                <MyButton
                  variant="contained"
                  color="primary"
                  className="w-[120px] font-semibold h-[56px]"
                  type="submit"
                >
                  가입하기
                </MyButton>
                <MyButton
                  variant="outlined"
                  className="w-[120px] h-[56px]"
                  onClick={backToLoginPage}
                >
                  <span className="text-gray-dark font-semibold">취소</span>
                </MyButton>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default Register;
